/**
 * Mock per-adapter shapes for the lobby's adapter panel stack.
 *
 * Mirrors the discriminator from the real `V2GridAsset` shape — `assetType`
 * + `platform` collapsed into a single `kind` string here — so the panel
 * components are forward-compatible with live data once the SDK is wired
 * into Phase 2. Each variant carries the smallest set of fields the
 * corresponding panel needs to render: anything the live API surfaces in
 * `V2GridAsset` (or follow-ups) can be threaded in later without changing
 * panel signatures.
 */

import { formatTakeoverUsd } from './mock-prices';

export type LobbyAdapterKind =
  | 'flaunch'
  | 'uniswap-v3'
  | 'uniswap-v4'
  | 'aerodrome'
  | 'custom';

export type LobbyAdapterStatus = 'live' | 'paused' | 'cooldown';

export interface AdapterLink {
  href: string;
  label: string;
  icon: 'flaunch' | 'uniswap' | 'dexscreener' | 'globe';
}

interface BaseAdapter {
  id: string;
  /** Display label override. Falls back to the kind's default name. */
  label?: string;
  subtitle: string;
  status: LobbyAdapterStatus;
  /** TVL formatted as a USD string (e.g. `"$612k"`). */
  tvlUsd: string;
  /** APR formatted as percentage string (e.g. `"47.2%"`). */
  apr: string;
  /** Yield accrued by the *board* in the displayed token, formatted. */
  accrued: string;
  accruedUsd: string;
  /** Outbound chrome — pool / chart / docs links. */
  links: AdapterLink[];
  /** Creator-authored layer from `pool_metadata.adapter_notes[id]`. */
  promo?: {
    title?: string;
    description?: string;
    imageUrl?: string;
    extraLinks: AdapterLink[];
  };
}

export interface FlaunchAdapter extends BaseAdapter {
  kind: 'flaunch';
  coinSymbol: string;
  coinAddress: string;
}

export interface UniswapV3Adapter extends BaseAdapter {
  kind: 'uniswap-v3';
  pair: string;
  /** Fee tier in basis points (100 / 500 / 3000 / 10000). */
  feeBps: number;
  isFullRange: boolean;
  currentTick?: number;
  tickLower?: number;
  tickUpper?: number;
}

export interface UniswapV4Adapter extends BaseAdapter {
  kind: 'uniswap-v4';
  pair: string;
  feeBps: number;
  /** Human-readable hook descriptor (e.g. "Anti-MEV", "Dynamic fee"). */
  hookSummary?: string;
}

export interface AerodromeAdapter extends BaseAdapter {
  kind: 'aerodrome';
  pair: string;
  /** Gauge weight as a formatted percent (e.g. `"3.2%"`). */
  gaugeWeightPct: string;
  /** veAERO bonus multiplier (e.g. `"2.4×"`). */
  veBoostMultiplier?: string;
}

export interface CustomAdapter extends BaseAdapter {
  kind: 'custom';
  contractName: string;
  /** Undefined while the indexer is still resolving `adapterAddress`
   *  for a freshly-registered asset; `CustomAdapterPanel` renders `—`
   *  and skips the explorer link in that case. Never fall back to the
   *  asset hash (PoolId) here — it would render as a bogus address. */
  contractAddress: string | undefined;
  /** Free-form headline metric (e.g. `"Fungal yield · 36 sporepods"`). */
  headlineMetric: string;
}

export type LobbyAdapter =
  | FlaunchAdapter
  | UniswapV3Adapter
  | UniswapV4Adapter
  | AerodromeAdapter
  | CustomAdapter;

export const ADAPTER_KIND_LABEL: Record<LobbyAdapterKind, string> = {
  flaunch: 'Flaunch',
  'uniswap-v3': 'Uniswap v3',
  'uniswap-v4': 'Uniswap v4',
  aerodrome: 'Aerodrome',
  custom: 'Custom',
};

/**
 * Brand colour map driving the left-edge accent stripe and icon background
 * tint on each adapter card. Hex values match the brand palettes used
 * elsewhere in the lobby (e.g. BoardCard hue list).
 */
export const ADAPTER_BRAND: Record<LobbyAdapterKind, { hue: string; bg: string; border: string }> = {
  flaunch:      { hue: '#ff7fbf', bg: 'rgba(255,127,191,0.10)', border: 'rgba(255,127,191,0.35)' },
  'uniswap-v3': { hue: '#ff8ac8', bg: 'rgba(255,138,200,0.10)', border: 'rgba(255,138,200,0.35)' },
  'uniswap-v4': { hue: '#a855f7', bg: 'rgba(168,85,247,0.12)',  border: 'rgba(168,85,247,0.40)' },
  aerodrome:    { hue: '#0ea5e9', bg: 'rgba(14,165,233,0.10)',  border: 'rgba(14,165,233,0.40)' },
  custom:       { hue: '#facc15', bg: 'rgba(250,204,21,0.10)',  border: 'rgba(250,204,21,0.40)' },
};

// ─── Mock data helpers ─────────────────────────────────────────────────────
//
// Every helper accepts `priceUsd` and uses `formatTakeoverUsd(amount,
// priceUsd)` so the rendered `accruedUsd` chip tracks the live
// TAKEOVER/USD rate threaded in from `useTakeoverPrice()` at the
// `useLobbyAdapters` hook level. The shimmed `takeoverToUsd()` /
// `MOCK_TAKEOVER_USD` fallback path no longer fires from this module
// in production — the dev warning that used to surface from the
// Boardroom fallback was the symptom that prompted this refactor.

function flaunch(
  id: string,
  priceUsd: number,
  overrides: Partial<FlaunchAdapter> & Pick<FlaunchAdapter, 'coinSymbol' | 'coinAddress' | 'tvlUsd' | 'apr' | 'accrued'>,
): FlaunchAdapter {
  return {
    id,
    kind: 'flaunch',
    // Subtitle was the noisy "$SYMBOL buybacks → seat rewards" tagline.
    // Replaced with a flat "Flaunch token" descriptor — the buyback /
    // seat-rewards mechanic lives in adapter docs now and the panel's
    // header + outbound Flaunch link already make the brand clear.
    subtitle: overrides.subtitle ?? 'Flaunch token',
    status: 'live',
    accruedUsd: formatTakeoverUsd(Number(overrides.accrued), priceUsd),
    // Single outbound link by design — the row used to also surface
    // Dexscreener, but Flaunch's own coin page already covers price /
    // chart / trade so doubling up was just visual noise. Label is the
    // bare brand ("Flaunch") because the chip now renders the text
    // alongside the icon.
    links: [{ href: '#', label: 'Flaunch', icon: 'flaunch' }],
    ...overrides,
  };
}

function uniV3(
  id: string,
  priceUsd: number,
  overrides: Partial<UniswapV3Adapter> & Pick<UniswapV3Adapter, 'pair' | 'feeBps' | 'tvlUsd' | 'apr' | 'accrued'>,
): UniswapV3Adapter {
  return {
    id,
    kind: 'uniswap-v3',
    subtitle: overrides.subtitle ?? `${overrides.pair} LP fees → seat deposits`,
    status: 'live',
    isFullRange: overrides.isFullRange ?? true,
    accruedUsd: formatTakeoverUsd(Number(overrides.accrued), priceUsd),
    links: [
      { href: '#', label: 'Uniswap pool', icon: 'uniswap' },
      { href: '#', label: 'Dexscreener', icon: 'dexscreener' },
    ],
    ...overrides,
  };
}

function uniV4(
  id: string,
  priceUsd: number,
  overrides: Partial<UniswapV4Adapter> & Pick<UniswapV4Adapter, 'pair' | 'feeBps' | 'tvlUsd' | 'apr' | 'accrued'>,
): UniswapV4Adapter {
  return {
    id,
    kind: 'uniswap-v4',
    subtitle: overrides.subtitle ?? `${overrides.pair} v4 hook fees → seats`,
    status: 'live',
    accruedUsd: formatTakeoverUsd(Number(overrides.accrued), priceUsd),
    links: [
      { href: '#', label: 'Uniswap pool', icon: 'uniswap' },
      { href: '#', label: 'Dexscreener', icon: 'dexscreener' },
    ],
    ...overrides,
  };
}

function aero(
  id: string,
  priceUsd: number,
  overrides: Partial<AerodromeAdapter> & Pick<AerodromeAdapter, 'pair' | 'gaugeWeightPct' | 'tvlUsd' | 'apr' | 'accrued'>,
): AerodromeAdapter {
  return {
    id,
    kind: 'aerodrome',
    subtitle: overrides.subtitle ?? `${overrides.pair} AERO gauge → seats`,
    status: 'live',
    accruedUsd: formatTakeoverUsd(Number(overrides.accrued), priceUsd),
    links: [{ href: '#', label: 'Aerodrome gauge', icon: 'globe' }],
    ...overrides,
  };
}

function custom(
  id: string,
  priceUsd: number,
  overrides: Partial<CustomAdapter> & Pick<CustomAdapter, 'contractName' | 'contractAddress' | 'headlineMetric' | 'tvlUsd' | 'apr' | 'accrued'>,
): CustomAdapter {
  return {
    id,
    kind: 'custom',
    subtitle: overrides.subtitle ?? `Custom adapter · ${overrides.contractName}`,
    status: 'live',
    accruedUsd: formatTakeoverUsd(Number(overrides.accrued), priceUsd),
    links: [{ href: '#', label: 'Contract', icon: 'globe' }],
    ...overrides,
  };
}

/**
 * Boardroom-only fallback adapter stack.
 *
 * This registry used to hold decorative adapter lists for every demo
 * board. Those were removed alongside the matching `MOCK_BOARDS`
 * entries once live grid data became the source of truth — all
 * community / curated boards now derive their adapters from
 * `V2GridAsset[]` via `gridAssetsToAdapters()`.
 *
 * The remaining `boardroom` entry is only reached when
 * `resolveLobbyLiveGridId('boardroom')` returns null (HV env vars
 * unset / early SSR / unit tests) so the flagship panel stack still
 * renders something instead of a blank card. Live Boardroom data
 * overrides this list as soon as `useLobbyGridPage` resolves.
 *
 * Built lazily at hook-call time so the `accruedUsd` strings can be
 * derived from the live TAKEOVER/USD price threaded in via
 * `useTakeoverPrice()` (the prior module-load const baked in
 * `MOCK_TAKEOVER_USD` and tripped the `mock-prices` fallback warning).
 */
export function buildMockBoardAdapters(priceUsd: number): Record<string, LobbyAdapter[]> {
  return {
    boardroom: [
      uniV4('boardroom.uni-v4', priceUsd, {
        pair: 'TAKEOVER / WETH',
        feeBps: 3000,
        tvlUsd: '$612k',
        apr: '47.2%',
        accrued: '12.40',
        hookSummary: 'Anti-MEV · dynamic fee',
      }),
      flaunch('boardroom.flaunch', priceUsd, {
        coinSymbol: 'TAKEOVER',
        coinAddress: '0xT4ke0ver',
        tvlUsd: '$128.4k',
        apr: '32.1%',
        accrued: '5.60',
      }),
    ],
  };
}

/**
 * Resolve adapters for a board id, with a sensible fallback so the panel
 * stack never blanks out on an unmapped board (newly-added boards default
 * to a single placeholder Custom adapter).
 *
 * `priceUsd` is the live TAKEOVER/USD rate from `useTakeoverPrice()`
 * — pass `0` only from static contexts (tests / SSR) where a hook
 * isn't reachable; the resulting `~$0.00` chip is the intended
 * "price unavailable" rendering.
 */
export function adaptersForBoard(
  boardId: string,
  fallbackName: string,
  priceUsd: number,
): LobbyAdapter[] {
  const direct = buildMockBoardAdapters(priceUsd)[boardId];
  if (direct && direct.length > 0) return direct;
  return [
    custom(`${boardId}.placeholder`, priceUsd, {
      contractName: fallbackName,
      contractAddress: '0x' + boardId.padEnd(40, '0').slice(0, 40),
      headlineMetric: 'No adapters registered',
      tvlUsd: '$0',
      apr: '0%',
      accrued: '0',
      status: 'cooldown',
    }),
  ];
}
